import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_URL from '../api';
import { Link } from 'react-router-dom';

// Font styles (copied from AboutPage.jsx for consistency)
const fontStyle = `
  @font-face {
    font-family: 'ArquitectaBold';
    src: url('/fonts/ArquitectaBold-webfont.eot');
    src: url('/fonts/ArquitectaBold-webfont.eot?#iefix') format('embedded-opentype'),
         url('/fonts/ArquitectaBold-webfont.woff') format('woff'),
         url('/fonts/ArquitectaBold-webfont.ttf') format('truetype'),
         url('/fonts/ArquitectaBold-webfont.svg#arquitecta_boldregular') format('svg');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrime';
    src: url('/fonts/courier_prime-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrimeItalic';
    src: url('/fonts/courier_prime_italic-webfont.woff') format('woff');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'BebasNeue';
    src: url('/fonts/bebasneue-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

const styleElement = document.createElement('style');
styleElement.textContent = fontStyle;
document.head.appendChild(styleElement);

const MENU_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Directors', href: '/directors' }, // This will be a dropdown trigger
  { label: 'Contact', href: '/contact' },
];

const sectionTitleStyle = {
  fontFamily: 'BebasNeue, sans-serif',
  fontSize: '2.5rem', // 40px
  color: '#ffffff',
  textAlign: 'center',
  marginBottom: '1.5rem', // 24px
  marginTop: '0', // Changed from 2rem to 0
};

const mainContactStyle = {
  textAlign: 'center',
  marginBottom: '3rem', // 48px
  fontSize: '1.125rem', // 18px
  lineHeight: '1.75', // 28px
};

const contactCardStyle = {
  marginBottom: '1rem', // 16px
  padding: '1rem', // 16px
  // border: '1px solid #333', // Removed border
  // borderRadius: '4px', // Removed border radius
};

export default function ContactPage() {
  const [directors, setDirectors] = useState([]);
  const [contactPageData, setContactPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuCloseTimeout = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);

  // Fetch directors for menu
  useEffect(() => {
    async function fetchAllDirectors() {
      const apiUrl = `${API_URL}/directors`;
      try {
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error(`Failed to fetch directors: ${res.status}`);
        const data = await res.json();
        setDirectors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error in fetchAllDirectors:', err);
        setDirectors([]);
      }
    }
    fetchAllDirectors();
  }, []);

  // Fetch contact page data
  useEffect(() => {
    async function fetchContactData() {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/contacts`);
        if (!response.ok) {
          throw new Error(`Failed to fetch contact data: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setContactPageData(data);
      } catch (err) {
        console.error('Error fetching contact data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchContactData();
  }, []);

  // Measure menu height
  useEffect(() => {
    if (menuRef.current && menuOpen) {
      setMenuHeight(menuRef.current.offsetHeight + 125); 
    }
  }, [menuOpen, directors]);

  // Close menu on click outside or Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuOpen && menuRef.current && menuButtonRef.current &&
          !menuRef.current.contains(event.target) &&
          !menuButtonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      clearTimeout(menuCloseTimeout.current);
    };
  }, [menuOpen]);

  const handleMenuEnter = useCallback(() => {
    clearTimeout(menuCloseTimeout.current);
    setIsMenuHovered(true);
  }, []);

  const handleMenuLeave = useCallback(() => {
    menuCloseTimeout.current = setTimeout(() => {
      if (!isHovering) {
        setIsMenuHovered(false);
        if (!isHovering) setMenuOpen(false);
      }
    }, 300);
  }, [isHovering]);

  const handleButtonEnter = useCallback(() => {
    clearTimeout(menuCloseTimeout.current);
    setIsHovering(true);
    setMenuOpen(true);
  }, []);

  const handleButtonLeave = useCallback(() => {
    setIsHovering(false);
    menuCloseTimeout.current = setTimeout(() => {
      if (!isMenuHovered) {
        setMenuOpen(false);
      }
    }, 300);
  }, [isMenuHovered]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
  }, []);

  return (
    <div style={{ minHeight: '100vh', width: '100%', backgroundColor: '#000000', color: '#ffffff', fontFamily: 'CourierPrime, monospace' }}>
      {/* Navbar Section (from AboutPage) */}
      <div 
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: menuOpen ? `translate(-50%, ${menuHeight}px)` : 'translate(-50%, 0)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            transition: 'transform 0.3s ease-in-out',
            transformOrigin: 'center top',
            willChange: 'transform',
            pointerEvents: 'none'
          }}
        >
          {/* Hamburger Menu Button (from AboutPage) */}
          <div 
            ref={menuButtonRef}
            onClick={toggleMenu} 
            onMouseEnter={handleButtonEnter}
            onMouseLeave={handleButtonLeave}
            style={{
              cursor: 'pointer',
              width: '24px',
              height: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '5px 0',
              boxSizing: 'content-box',
              pointerEvents: 'auto'
            }}
          >
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              opacity: menuOpen ? 0 : 1,
              transform: menuOpen ? 'translateX(20px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'
            }}></span>
          </div>

          {/* Page Title in Navbar (from AboutPage) */}
          <Link to="/" style={{
            display: 'block',
            height: 'auto',
            transition: 'transform 0.3s ease-in-out',
            pointerEvents: 'auto',
            textDecoration: 'none',
            color: '#fff',
            fontSize: '28px',
            fontFamily: 'CourierPrime, monospace',
            fontWeight: 'normal',
            lineHeight: '1',
            paddingTop: '2px'
          }}>
            Contact Us
          </Link>
        </div>

      {/* Menu Panel (from AboutPage) */}
      <div 
        ref={menuRef}
        style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          backgroundColor: '#fff',
          zIndex: 998,
          padding: '40px 0 10px',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'auto' : 'none',
          transform: menuOpen ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'all 0.3s ease-in-out',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          maxHeight: menuOpen ? '100vh' : '0px',
          visibility: menuOpen ? 'visible' : 'hidden',
        }}
        onMouseEnter={handleMenuEnter}
        onMouseLeave={handleMenuLeave}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ marginBottom: '15px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 'normal', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 15px', color: '#666', fontFamily: 'BebasNeue, sans-serif' }}>Directors</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
              {directors.filter(d => d.active).map(director => (
                <Link 
                  key={director.id} 
                  to={`/directors/${director.id}`} 
                  onClick={() => setMenuOpen(false)}
                  style={{ color: '#333', textDecoration: 'none', fontSize: '20px', margin: '0 8px', fontWeight: 'normal', fontFamily: 'BebasNeue, sans-serif', lineHeight: '1.2', transition: 'color 0.2s', opacity: 0.8 }}
                  onMouseOver={e => e.currentTarget.style.opacity = '1'}
                  onMouseOut={e => e.currentTarget.style.opacity = '0.8'}
                >
                  {director.first_name} {director.last_name}
                </Link>
              ))}
            </div>
            <div style={{ width: '100%', height: '1px', backgroundColor: '#eee', margin: '15px auto', maxWidth: '500px' }}></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginBottom: '10px', justifyContent: 'center', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', padding: '0 20px' }}>
            {MENU_LINKS.filter(link => link.label !== 'Directors').map(link => (
              <Link 
                key={link.label} 
                to={link.href} 
                onClick={() => setMenuOpen(false)}
                style={{ color: 'rgba(0,0,0,0.8)', textDecoration: 'none', fontSize: '16px', fontFamily: 'BebasNeue, sans-serif', textTransform: 'uppercase', letterSpacing: '1px', padding: '3px 0', position: 'relative', transition: 'color 0.2s' }}
                onMouseOver={e => e.currentTarget.style.color = 'rgba(0,0,0,1)'}
                onMouseOut={e => e.currentTarget.style.color = 'rgba(0,0,0,0.8)'}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main Page Content Area */}
      <main style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '150px' }}>
        <div className="contact-content-container w-full max-w-5xl mx-auto" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
          <h1 style={sectionTitleStyle}>Get In Touch</h1>

          {loading && <div style={{ color: 'white', fontSize: '1.5rem', textAlign: 'center', marginTop: '50px' }}>Loading contact information...</div>}
          {error && <div style={{ color: 'red', fontSize: '1.5rem', textAlign: 'center', marginTop: '50px' }}>Error: {error}</div>}

          {contactPageData && !loading && !error && (
            <>
              {/* Main Contact Info */}
              {contactPageData.mainContactInfo && (
                <div style={mainContactStyle}>
                  <p>{contactPageData.mainContactInfo.address}</p>
                  <p>Phone: {contactPageData.mainContactInfo.phone}</p>
                  <p>Email: <a href={`mailto:${contactPageData.mainContactInfo.email}`} className="hover:underline">{contactPageData.mainContactInfo.email}</a></p>
                </div>
              )}

              {/* Categorized Contacts */}
              {contactPageData.categories && contactPageData.categories.length > 0 && (
                <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {contactPageData.categories.map((category) => (
                    <div key={category.id || category.name} className="flex flex-col items-center md:items-start">
                      <h2 
                        style={{
                          fontFamily: 'BebasNeue, sans-serif',
                          fontSize: '1.875rem', // 30px
                          color: '#ffffff',
                          marginBottom: '1rem', // 16px
                          // textAlign: 'center', // Removed for left alignment
                          width: '100%'
                        }}
                      >
                        {category.name}
                      </h2>
                      {category.contacts && category.contacts.map(contact => (
                        <div key={contact.id || contact.title} style={contactCardStyle} className="w-full text-center md:text-left">
                          <h3 style={{ fontFamily: 'BebasNeue, sans-serif', fontSize: '1.25rem', marginBottom: '0.5rem' }}>{contact.title}</h3>
                          {contact.details && contact.details.map((detail, idx) => (
                            <p key={idx} style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>{detail}</p>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

        </div>
      </main>
    </div>
  );
}
